import { useState, useEffect, useRef } from 'react';
import { Card, CardBody, Button, Input, Modal, ModalBody, Badge } from '@components/ui';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@components/ui';
import { PlusIcon, TrashIcon, PencilIcon, MagnifyingGlassIcon, CubeIcon } from '@heroicons/react/24/outline';
import { useProducts } from '@features/products/hooks/useProducts';
import { ProductFormModal } from './ProductFormModal';
import { formatNumber, roundPrice, formatPriceDecimals, formatPriceDisplay } from '@/utils/format';
import { OTI_CODES, SEGMENTS, getFamiliesBySegment } from '@features/products/constants/taxCategories';
import type { InvoiceWizardData, InvoiceItem, OTIItem } from '../../types/invoiceWizard.types';
import { showWarning } from '@/utils/sweetAlert';

// ISC (Impuesto Selectivo al Consumo) - Predefined rates
const ISC_RATES = [
  {
    value: 0.05,
    description: "Impuesto Selectivo al Consumo de Gaseosas; Impuesto Selectivo al Consumo de Joyas y Armas de Fuego; Impuesto Selectivo al Consumo de televisión por cable, microondas y satelital, telefonía móvil; Impuesto Selectivo al Consumo de Bebidas Alcohólicas (B/. 0.05 por litro según detalle); Impuesto Selectivo al Consumo de Vinos (B/. 0.05 por litro según detalle)"
  },
  {
    value: 0.06,
    description: "Jarabes, siropes o concentrados"
  },
  {
    value: 0.00,
    description: "Impuesto Selectivo al Consumo de Bebidas Espirituosas"
  },
  {
    value: 0.1325,
    description: "Impuesto Selectivo al Consumo de Cervezas (B/. 0.1325 por litro según detalle)"
  },
  {
    value: 0.325,
    description: "Impuesto Selectivo al Consumo de Cigarrillos y tabacos"
  },
  {
    value: 0.035,
    description: "Impuesto Selectivo al Consumo de Licores"
  },
  {
    value: 0.00,
    description: "Impuesto Selectivo al Consumo de Minería no metálica"
  }
];

interface Step3AddItemsProps {
  wizardData: InvoiceWizardData;
  onAddItem: (item: InvoiceItem) => void;
  onUpdateItem: (index: number, item: InvoiceItem) => void;
  onRemoveItem: (index: number) => void;
  onNext: () => void;
  onPrevious: () => void;
}

interface ItemFormData {
  productId: string;
  reference: string;
  productCode: string;
  productName: string;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  unitDiscount: number;
  taxRate: number;
  iscRate: number;
  otiAmount: number;
  gOTIItem: OTIItem[]; // Array de OTI por item
  shippingAmount: number;
  insuranceAmount: number;
  orderNumber?: string;
  orderItemSequence?: string;
  orderItemInfo?: string;
  segment?: string;
  family?: string;
}

export function Step3AddItems({
  wizardData,
  onAddItem,
  onUpdateItem,
  onRemoveItem,
  onNext,
  onPrevious,
}: Step3AddItemsProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [isCreateProductModalOpen, setIsCreateProductModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [availableFamilies, setAvailableFamilies] = useState<any[]>([]);
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const [itemErrors, setItemErrors] = useState<Record<string, string>>({});
  const orderDetailsRef = useRef<HTMLDetailsElement>(null);
  const orderNumberRef = useRef<HTMLInputElement>(null);
  const { data: productsData, isLoading: isLoadingProducts, error: productsError } = useProducts({ page: 1, limit: 100, name: search });

  // Debug log
  console.log('🛍️ Products Data:', {
    showProductSearch,
    isLoadingProducts,
    productsError,
    productsCount: productsData?.products?.length,
    totalProducts: productsData?.total,
    productsData
  });

  const [itemForm, setItemForm] = useState<ItemFormData>({
    productId: '',
    reference: '',
    productCode: '',
    productName: '',
    description: '',
    quantity: 1,
    unit: 'und', // Default unit
    unitPrice: 0,
    unitDiscount: 0,
    taxRate: 7, // ITBMS default 7%
    iscRate: 0,
    otiAmount: 0,
    gOTIItem: [], // Array de OTI vacío inicialmente
    shippingAmount: 0,
    insuranceAmount: 0,
    orderNumber: '',
    orderItemSequence: '',
    orderItemInfo: '',
  });

  const [calculatedValues, setCalculatedValues] = useState({
    subtotal: 0,
    discountTotal: 0,
    taxAmount: 0,
    iscAmount: 0,
    total: 0,
  });

  // Validación en vivo: descuento no puede exceder precio (DGI C202) + C202a
  useEffect(() => {
    const price = Number(itemForm.unitPrice) || 0;
    const discount = Number(itemForm.unitDiscount) || 0;
    if (discount > 0 && price === 0) {
      // DGI C202a (2052): Descuento > 0 cuando precio = 0 → R
      setItemErrors(prev => prev.unitDiscount === 'No puede tener descuento cuando el precio unitario es 0 (DGI C202a código 2052)' ? prev : { unitDiscount: 'No puede tener descuento cuando el precio unitario es 0 (DGI C202a código 2052)' });
    } else if (discount > 0 && discount > price) {
      setItemErrors(prev => prev.unitDiscount === 'El descuento no puede ser mayor al precio unitario (DGI C202)' ? prev : { unitDiscount: 'El descuento no puede ser mayor al precio unitario (DGI C202)' });
    } else {
      setItemErrors(prev => prev.unitDiscount ? {} : prev);
    }
  }, [itemForm.unitPrice, itemForm.unitDiscount]);

  // Calcular totales cuando cambian los valores del formulario
  useEffect(() => {
    // Asegurar que todos los valores son números válidos
    const quantity = Number(itemForm.quantity) || 0;
    const unitPrice = Number(itemForm.unitPrice) || 0;
    const unitDiscount = Number(itemForm.unitDiscount) || 0;
    const taxRate = Number(itemForm.taxRate) || 0;
    const iscRate = Number(itemForm.iscRate) || 0;
    const shippingAmount = Number(itemForm.shippingAmount) || 0;
    const insuranceAmount = Number(itemForm.insuranceAmount) || 0;

    // Calcular suma de OTI desde el array gOTIItem
    const otiAmount = itemForm.gOTIItem.reduce((sum, oti) => {
      return sum + (parseFloat(oti.dValOTI) || 0);
    }, 0);

    // Subtotal = (Precio unitario - Descuento unitario) * Cantidad
    // roundPrice preserva hasta 6 decimales (DGI 1-9p2-6) para dPrItem
    const subtotal = roundPrice((unitPrice - unitDiscount) * quantity);
    const discountTotal = roundPrice(unitDiscount * quantity);

    // ISC se calcula sobre el subtotal (iscRate ya es decimal, no porcentaje)
    // roundPrice para impuestos por item (DGI: dValISC 1-9p2-6, C502 = C203 * C501)
    const iscAmount = roundPrice(subtotal * iscRate);

    // ITBMS se calcula sobre el subtotal (NO incluir ISC)
    // DGI validación 2152: C402 = C203 * C401 (dValITBMS = dPrItem * tasa)
    const taxAmount = roundPrice((subtotal * taxRate) / 100);

    // Total = Subtotal + ISC + ITBMS + OTI + Acarreo + Seguro
    // roundPrice preserva hasta 6 decimales (DGI 1-9p2-6) para dValTotItem
    const total = roundPrice(subtotal + iscAmount + taxAmount + otiAmount + shippingAmount + insuranceAmount);

    setCalculatedValues({
      subtotal,
      discountTotal,
      taxAmount,
      iscAmount,
      total,
    });
  }, [itemForm]);

  // Update families when segment changes
  useEffect(() => {
    if (itemForm.segment) {
      const families = getFamiliesBySegment(itemForm.segment);
      setAvailableFamilies(families);
    } else {
      setAvailableFamilies([]);
      // Clear family if segment is cleared
      if (itemForm.family) {
        updateField('family', '');
      }
    }
  }, [itemForm.segment]);

  const handleSelectProduct = (product: any) => {
    // Map taxCode to taxRate percentage
    const getTaxRate = (taxCode: string): number => {
      if (!taxCode) return 7;
      if (taxCode.includes('18')) return 18;
      if (taxCode.includes('15')) return 15;
      if (taxCode.includes('10')) return 10;
      if (taxCode.includes('7')) return 7;
      if (taxCode.includes('NOTAX') || taxCode.includes('0')) return 0;
      return 7; // Default
    };

    setItemForm({
      ...itemForm,
      productId: product.id,
      productCode: product.sku || product.code, // Use sku from backend
      productName: product.name,
      description: product.description || '',
      unit: product.unitCode || 'und', // Use unit from product or default to 'und'
      unitPrice: product.price || product.unitPrice || 0, // Use price from backend
      taxRate: getTaxRate(product.taxCode),
      segment: product.segment,
      family: product.family,
    });
    setShowProductSearch(false);
    setSearch('');
  };

  const handleSaveItem = () => {
    const newItemErrors: Record<string, string> = {};

    // Validación DGI: máximo 1,000 ítems por documento
    if (editingIndex === null && wizardData.items.length >= 1000) {
      newItemErrors.maxItems = 'No se pueden agregar más de 1,000 ítems por documento (DGI)';
    }

    // Validación: nombre del producto obligatorio
    if (!itemForm.productName?.trim()) {
      newItemErrors.productName = 'El nombre del producto/servicio es obligatorio';
    }

    // Validación: cantidad debe ser > 0
    if (!itemForm.quantity || itemForm.quantity <= 0) {
      newItemErrors.quantity = 'La cantidad debe ser mayor a 0';
    }

    // Validación DGI C201 (2050): precio unitario muy elevado (advertencia, no bloquea)
    const numericPrice = Number(itemForm.unitPrice) || 0;
    const numericDiscount = Number(itemForm.unitDiscount) || 0;

    if (numericPrice > 100000) {
      newItemErrors.unitPrice = 'Precio unitario superior a B/100,000 (DGI C201 - advertencia)';
    }

    // Validación DGI C202a (2052): descuento cuando precio = 0
    if (numericDiscount > 0 && numericPrice === 0) {
      newItemErrors.unitDiscount = 'No puede tener descuento cuando el precio unitario es 0 (DGI C202a)';
    }

    // Validación DGI C202 (2051): descuento no puede exceder precio unitario
    if (numericDiscount > numericPrice) {
      newItemErrors.unitDiscount = 'El descuento no puede ser mayor al precio unitario (DGI C202)';
    }

    // Validación DGI C10 (2006): CPBS (Segmento + Familia) obligatorio si receptor = Gobierno.
    // Antes solo se exigía cuando faltaban AMBOS campos, por lo que un ítem con Segmento
    // seleccionado pero sin Familia pasaba esta validación y luego Aludra/DGI rechazaba
    // la factura al emitir (error 2006). Ahora se exige Familia también, avisando al
    // usuario en el momento de agregar el ítem (no hasta emitir la factura).
    if (wizardData.receiverType === '03' && !itemForm.segment) {
      newItemErrors.segment = 'Código CPBS obligatorio para ventas al Gobierno (DGI C10)';
      showWarning('Segmento requerido', 'Debe seleccionar el Segmento de la Codificación Panameña de Bienes y Servicios (CPBS) para facturar a una entidad de Gobierno.');
    } else if (wizardData.receiverType === '03' && !itemForm.family) {
      newItemErrors.family = 'Debe seleccionar la Familia del código CPBS para ventas al Gobierno (DGI C10)';
      showWarning('Familia requerida', 'Debe seleccionar la Familia de la Codificación Panameña de Bienes y Servicios (CPBS) para facturar a una entidad de Gobierno.');
    }

    // Si hay errores bloqueantes (no advertencias), detener
    const blockingErrors = Object.entries(newItemErrors).filter(([key]) => key !== 'unitPrice');
    if (blockingErrors.length > 0) {
      setItemErrors(newItemErrors);
      return;
    }

    // Validación DGI C202a (2052): descuento cuando precio = 0 (ya cubierto arriba)
    // Validación DGI C202: descuento no puede exceder precio unitario (ya cubierto arriba)
    // Validación DGI: si llena cualquier campo de pedido, todos son obligatorios
    const hasAnyOrderField = !!(itemForm.orderNumber || itemForm.orderItemSequence || itemForm.orderItemInfo);
    if (hasAnyOrderField) {
      const orderErrors: Record<string, string> = {};
      if (!itemForm.orderNumber) orderErrors.orderNumber = 'Requerido';
      if (!itemForm.orderItemSequence) orderErrors.orderItemSequence = 'Requerido';
      if (!itemForm.orderItemInfo) orderErrors.orderItemInfo = 'Requerido';

      if (Object.keys(orderErrors).length > 0) {
        setItemErrors(orderErrors);
        if (orderDetailsRef.current) {
          orderDetailsRef.current.open = true;
        }
        setTimeout(() => {
          orderNumberRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
        showWarning('Campos requeridos', 'Si completa un campo de pedido, debe llenar todos los campos de la sección.');
        return;
      }
    }
    setItemErrors({});

    // Calcular suma total de OTI para otiAmount (retrocompatibilidad)
    const totalOtiAmount = itemForm.gOTIItem.reduce((sum, oti) => {
      return sum + (parseFloat(oti.dValOTI) || 0);
    }, 0);

    // Ensure all numeric fields are actual numbers (not strings from text inputs)
    const safePrice = Number(itemForm.unitPrice) || 0;
    const safeDiscount = Number(itemForm.unitDiscount) || 0;
    const safeQuantity = Number(itemForm.quantity) || 0;
    const safeShipping = Number(itemForm.shippingAmount) || 0;
    const safeInsurance = Number(itemForm.insuranceAmount) || 0;

    const newItem: InvoiceItem = {
      id: editingIndex !== null ? wizardData.items[editingIndex].id : `${Date.now()}-${Math.random()}`,
      productId: itemForm.productId,
      reference: itemForm.reference,
      productCode: itemForm.productCode,
      productName: itemForm.productName,
      description: itemForm.description,
      quantity: safeQuantity,
      unit: itemForm.unit || 'und', // Include unit with fallback to 'und'
      unitPrice: safePrice,
      unitDiscount: safeDiscount,
      discountPercent: safePrice > 0 ? (safeDiscount / safePrice) * 100 : 0,
      taxRate: Number(itemForm.taxRate) || 0,
      taxAmount: calculatedValues.taxAmount,
      iscRate: Number(itemForm.iscRate) || 0,
      iscAmount: calculatedValues.iscAmount,
      otiAmount: totalOtiAmount, // Suma total de OTI
      gOTIItem: itemForm.gOTIItem.length > 0 ? itemForm.gOTIItem : undefined, // Solo incluir si hay OTI
      shippingAmount: safeShipping,
      insuranceAmount: safeInsurance,
      orderNumber: itemForm.orderNumber,
      orderItemSequence: itemForm.orderItemSequence,
      orderItemInfo: itemForm.orderItemInfo,
      segment: itemForm.segment,
      family: itemForm.family,
      subtotal: calculatedValues.subtotal,
      total: calculatedValues.total,
    };

    if (editingIndex !== null) {
      onUpdateItem(editingIndex, newItem);
      setEditingIndex(null);
    } else {
      onAddItem(newItem);
    }

    handleCloseModal();
  };

  const handleOpenModal = () => {
    setEditingIndex(null);
    setItemForm({
      productId: '',
      reference: '',
      productCode: '',
      productName: '',
      description: '',
      quantity: 1,
      unit: 'und',
      unitPrice: 0,
      unitDiscount: 0,
      taxRate: 7,
      iscRate: 0,
      otiAmount: 0,
      gOTIItem: [], // Array vacío para nuevo item
      shippingAmount: 0,
      insuranceAmount: 0,
      orderNumber: '',
      orderItemSequence: '',
      orderItemInfo: '',
      segment: '',
      family: '',
    });
    setShowProductSearch(false);
    setAvailableFamilies([]);
    setIsModalOpen(true);
  };

  const handleEditItem = (index: number) => {
    const item = wizardData.items[index];
    setEditingIndex(index);
    setItemForm({
      productId: item.productId,
      reference: item.reference,
      productCode: item.productCode,
      productName: item.productName,
      description: item.description,
      quantity: item.quantity,
      unit: item.unit || 'und',
      unitPrice: item.unitPrice,
      unitDiscount: item.unitDiscount,
      taxRate: item.taxRate,
      iscRate: item.iscRate,
      otiAmount: item.otiAmount,
      gOTIItem: item.gOTIItem || [], // Cargar OTI existentes o array vacío
      shippingAmount: item.shippingAmount || 0,
      insuranceAmount: item.insuranceAmount || 0,
      orderNumber: item.orderNumber || '',
      orderItemSequence: item.orderItemSequence || '',
      orderItemInfo: item.orderItemInfo || '',
      segment: item.segment || '',
      family: item.family || '',
    });

    // Initialize families if segment exists
    if (item.segment) {
      const families = getFamiliesBySegment(item.segment);
      setAvailableFamilies(families);
    } else {
      setAvailableFamilies([]);
    }

    setShowProductSearch(false);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    console.log('🚨 handleCloseModal called - Checking if should close');
    // Don't close if the create product modal is open
    if (isCreateProductModalOpen) {
      console.log('⚠️ Create product modal is open, NOT closing main modal');
      return;
    }
    console.log('✅ Closing main modal');
    setIsModalOpen(false);
    setShowProductSearch(false);
    setSearch('');
  };

  const updateField = (field: keyof ItemFormData, value: string | number) => {
    setItemForm((prev) => ({ ...prev, [field]: value }));
  };

  // Funciones para manejar OTI
  const handleAddOTI = () => {
    setItemForm((prev) => {
      // DGI C601 (2300): No permitir código OTI duplicado en mismo ítem
      const usedCodes = prev.gOTIItem.map(oti => oti.dCodOTI);
      const availableCode = OTI_CODES.find(c => !usedCodes.includes(c.code));
      if (!availableCode) {
        showWarning('Límite de OTI', 'Ya se han agregado todos los códigos OTI disponibles para este ítem (DGI C601).');
        return prev;
      }
      return {
        ...prev,
        gOTIItem: [...prev.gOTIItem, { dCodOTI: availableCode.code, dValOTI: '0.00' }]
      };
    });
  };

  const handleRemoveOTI = (index: number) => {
    setItemForm((prev) => ({
      ...prev,
      gOTIItem: prev.gOTIItem.filter((_, i) => i !== index)
    }));
  };

  const handleUpdateOTI = (index: number, field: keyof OTIItem, value: string) => {
    setItemForm((prev) => ({
      ...prev,
      gOTIItem: prev.gOTIItem.map((oti, i) =>
        i === index ? { ...oti, [field]: value } : oti
      )
    }));
  };

  return (
    <div className="space-y-6" data-testid="invoice-wizard-step3-container">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-secondary-900 dark:text-white">Agregar Items</h2>
          <p className="mt-2 text-secondary-600 dark:text-secondary-400">
            Agrega productos o servicios a la factura
          </p>
        </div>
        <div className="flex items-center gap-3">
          {wizardData.items.length >= 1000 && (
            <span className="text-xs text-danger-600 dark:text-danger-400">Límite de 1,000 ítems alcanzado</span>
          )}
          <Button leftIcon={<PlusIcon className="w-5 h-5" />} onClick={handleOpenModal} disabled={wizardData.items.length >= 1000} data-testid="invoice-wizard-step3-add-item-button">
            Agregar Item
          </Button>
        </div>
      </div>

      <Card>
        <CardBody className="p-0">
          {wizardData.items.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-white">
                No hay items
              </h3>
              <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
                Comienza agregando productos o servicios
              </p>
              <div className="mt-6">
                <Button onClick={handleOpenModal} data-testid="invoice-wizard-step3-add-first-item-button">
                  <PlusIcon className="w-5 h-5 mr-2" />
                  Agregar Primer Item
                </Button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="invoice-wizard-step3-items-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Referencia</TableHead>
                    <TableHead>Código</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead className="text-right">Cant.</TableHead>
                    <TableHead className="text-right">Precio Unit.</TableHead>
                    <TableHead className="text-right">Desc. Unit.</TableHead>
                    <TableHead className="text-right">ITBMS</TableHead>
                    <TableHead className="text-right">ISC</TableHead>
                    <TableHead className="text-right">OTI</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {wizardData.items.map((item, index) => (
                    <TableRow key={item.id} data-testid={`invoice-wizard-step3-item-row-${index}`}>
                      <TableCell className="font-medium">{item.reference || '-'}</TableCell>
                      <TableCell>{item.productCode}</TableCell>
                      <TableCell>
                        <div className="min-w-[200px] max-w-[400px]">
                          <div className="font-medium whitespace-normal">{item.productName}</div>
                          {item.description && (
                            <div className="text-sm text-secondary-500 whitespace-normal">
                              {item.description}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{formatNumber(item.quantity, 2)}</TableCell>
                      <TableCell className="text-right">${formatPriceDisplay(item.unitPrice)}</TableCell>
                      <TableCell className="text-right">${formatPriceDisplay(item.unitDiscount)}</TableCell>
                      <TableCell className="text-right">
                        <div className="text-xs text-secondary-500">{item.taxRate}%</div>
                        <div>${formatPriceDisplay(item.taxAmount)}</div>
                      </TableCell>
                      <TableCell className="text-right">
                        {item.iscRate > 0 ? (
                          <>
                            <div className="text-xs text-secondary-500">{(item.iscRate * 100).toFixed(2)}%</div>
                            <div>${formatPriceDisplay(item.iscAmount)}</div>
                          </>
                        ) : (
                          <span className="text-secondary-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {item.gOTIItem && item.gOTIItem.length > 0 ? (
                          <div className="min-w-[120px]">
                            {item.gOTIItem.map((oti, otiIdx) => {
                              const otiCode = OTI_CODES.find(c => c.code === oti.dCodOTI);
                              return (
                                <div key={otiIdx} className="text-xs text-secondary-500">
                                  {otiCode?.code}: ${formatNumber(parseFloat(oti.dValOTI))}
                                </div>
                              );
                            })}
                            <div className="font-semibold mt-1 border-t border-secondary-200 pt-1">
                              ${formatNumber(item.otiAmount)}
                            </div>
                          </div>
                        ) : item.otiAmount > 0 ? (
                          `$${formatNumber(item.otiAmount)}`
                        ) : (
                          <span className="text-secondary-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-semibold">${formatPriceDisplay(item.total)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleEditItem(index)}
                            className="text-primary-600 hover:text-primary-900"
                            title="Editar"
                            data-testid={`invoice-wizard-step3-edit-item-button-${index}`}
                          >
                            <PencilIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => onRemoveItem(index)}
                            className="text-danger-600 hover:text-danger-900"
                            title="Eliminar"
                            data-testid={`invoice-wizard-step3-remove-item-button-${index}`}
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      {wizardData.items.length > 0 && (
        <Card>
          <CardBody>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-secondary-600 dark:text-secondary-400">Subtotal:</span>
                <span className="font-medium">${formatNumber(wizardData.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-secondary-600 dark:text-secondary-400">Descuentos:</span>
                <span className="font-medium text-danger-600">-${formatNumber(wizardData.totalDiscount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-secondary-600 dark:text-secondary-400">Impuestos (ITBMS):</span>
                <span className="font-medium">${formatNumber(wizardData.totalTax)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold pt-3 border-t border-secondary-200 dark:border-secondary-700">
                <span>Total:</span>
                <span className="text-primary-600">${formatNumber(wizardData.grandTotal)}</span>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrevious} data-testid="invoice-wizard-step3-previous-button">
          Anterior
        </Button>
        <Button variant="primary" onClick={onNext} disabled={wizardData.items.length === 0} data-testid="invoice-wizard-step3-next-button">
          Siguiente
        </Button>
      </div>

      {/* Modal para agregar/editar item */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        kicker="Factura · Detalle"
        title={editingIndex !== null ? 'Editar Item' : 'Agregar Item'}
        icon={<CubeIcon />}
        size="full"
        data-testid="invoice-wizard-step3-item-modal"
      >
        <ModalBody className="relative">
          {/* Indicador de scroll flotante - solo muestra si no se ha hecho scroll */}
          {showScrollIndicator && !showProductSearch && (
            <div className="absolute bottom-0 left-0 right-0 pointer-events-none z-30">
              <div className="h-32 bg-gradient-to-t from-white dark:from-secondary-900 via-white/70 dark:via-secondary-900/70 to-transparent"></div>
              <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center animate-bounce">
                <div className="bg-primary-600 dark:bg-primary-500 text-white px-4 py-2 rounded-full shadow-xl flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  <span className="text-sm font-medium">Desplázate para ver más campos</span>
                </div>
              </div>
            </div>
          )}

          <div
            className="flex flex-col lg:flex-row gap-6 max-h-[85vh] overflow-y-auto"
            onScroll={(e) => {
              const element = e.currentTarget;
              // Si el usuario ha hecho scroll más de 50px, ocultar el indicador
              if (element.scrollTop > 50) {
                setShowScrollIndicator(false);
              } else {
                // Si vuelve arriba, mostrar el indicador de nuevo si hay contenido que no se ve
                const hasScrollableContent = element.scrollHeight > element.clientHeight;
                setShowScrollIndicator(hasScrollableContent);
              }
            }}
          >
            {/* Columna Izquierda - Formulario */}
            {/* space-y-4 (antes 6): mismo formulario, menos aire entre cada
                grupo de campos — con ~8 secciones apiladas, 24px c/u sumaba
                bastante alto extra al modal. */}
            <div className="flex-1 space-y-4 pr-4 pb-6">
            {/* Selector de producto */}
            {!showProductSearch && !itemForm.productId && (
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowProductSearch(true)}
                  leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
                  className="flex-1"
                  data-testid="invoice-wizard-step3-search-catalog-button"
                >
                  Buscar desde catálogo
                </Button>
                <Button
                  variant="success"
                  onClick={() => setIsCreateProductModalOpen(true)}
                  leftIcon={<PlusIcon className="w-5 h-5" />}
                  className="flex-1"
                  data-testid="invoice-wizard-step3-create-product-button"
                >
                  Crear Producto
                </Button>
              </div>
            )}

            {/* Búsqueda de productos */}
            {showProductSearch && (
              <Card>
                <CardBody>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <MagnifyingGlassIcon className="w-5 h-5 text-secondary-400" />
                      <Input
                        type="text"
                        placeholder="Buscar por nombre o código..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="flex-1"
                        data-testid="invoice-wizard-step3-product-search-input"
                      />
                      <Button variant="outline" onClick={() => setShowProductSearch(false)} data-testid="invoice-wizard-step3-cancel-search-button">
                        Cancelar
                      </Button>
                    </div>

                    {/* Grid de productos - Diseño moderno 2025 */}
                    <div className="h-[550px] overflow-y-auto bg-secondary-50 dark:bg-secondary-900/50 rounded-lg p-4">
                      {isLoadingProducts ? (
                        <div className="flex items-center justify-center h-full">
                          <div className="text-center">
                            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600"></div>
                            <p className="mt-4 text-sm font-medium text-secondary-600 dark:text-secondary-400">Cargando catálogo...</p>
                          </div>
                        </div>
                      ) : productsError ? (
                        <div className="flex items-center justify-center h-full">
                          <div className="text-center">
                            <div className="mx-auto h-16 w-16 text-danger-400">
                              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                            </div>
                            <p className="mt-4 text-base font-medium text-secondary-900 dark:text-white">Error al cargar productos</p>
                            <p className="mt-1 text-sm text-secondary-500">{productsError instanceof Error ? productsError.message : 'Error desconocido'}</p>
                          </div>
                        </div>
                      ) : productsData?.products ? (
                        <>
                          {productsData.products.filter((p) => !p.isDeleted).length === 0 ? (
                            <div className="flex items-center justify-center h-full">
                              <div className="text-center">
                                <div className="mx-auto h-16 w-16 text-secondary-300 dark:text-secondary-600">
                                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                  </svg>
                                </div>
                                <p className="mt-4 text-base font-medium text-secondary-900 dark:text-white">
                                  {search ? 'No encontramos productos' : 'Catálogo vacío'}
                                </p>
                                <p className="mt-1 text-sm text-secondary-500">
                                  {search ? 'Intenta con otros términos de búsqueda' : 'Aún no tienes productos en tu catálogo'}
                                </p>
                              </div>
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {productsData.products.filter((p) => !p.isDeleted).map((product) => {
                                // Extract tax rate from taxCode
                                const getTaxRate = (taxCode: string): string => {
                                  if (!taxCode) return '7';
                                  if (taxCode.includes('18')) return '18';
                                  if (taxCode.includes('15')) return '15';
                                  if (taxCode.includes('10')) return '10';
                                  if (taxCode.includes('7')) return '7';
                                  if (taxCode.includes('NOTAX') || taxCode.includes('0')) return '0';
                                  return '7';
                                };

                                return (
                                  <button
                                    key={product.id}
                                    onClick={() => handleSelectProduct(product)}
                                    className="group relative bg-white dark:bg-secondary-800 rounded-xl p-4 border-2 border-secondary-200 dark:border-secondary-700 hover:border-primary-500 hover:shadow-lg transition-all duration-200 text-left"
                                    data-testid={`invoice-wizard-step3-product-card-button-${product.id}`}
                                  >
                                    {/* Badge de categoría */}
                                    <div className="absolute top-3 right-3">
                                      <Badge variant={product.isService ? 'info' : 'default'} size="sm">
                                        {product.category || 'General'}
                                      </Badge>
                                    </div>

                                    {/* Contenido principal */}
                                    <div className="pr-20">
                                      {/* Nombre del producto */}
                                      <h3 className="text-base font-bold text-secondary-900 dark:text-white line-clamp-2 mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                                        {product.name}
                                      </h3>

                                      {/* SKU */}
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="text-xs font-mono px-2 py-0.5 bg-secondary-100 dark:bg-secondary-700 text-secondary-600 dark:text-secondary-300 rounded">
                                          {product.sku}
                                        </span>
                                      </div>

                                      {/* Descripción */}
                                      {product.description && (
                                        <p className="text-xs text-secondary-500 dark:text-secondary-400 line-clamp-2 mb-3">
                                          {product.description}
                                        </p>
                                      )}

                                      {/* Footer con precio e impuesto */}
                                      <div className="flex items-end justify-between mt-3 pt-3 border-t border-secondary-100 dark:border-secondary-700">
                                        <div>
                                          <div className="text-xs text-secondary-500 dark:text-secondary-400 mb-1">Precio</div>
                                          <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                                            ${product.price.toFixed(2)}
                                          </div>
                                          <div className="text-xs text-secondary-400">{product.currency || 'USD'}</div>
                                        </div>
                                        <div className="text-right">
                                          <div className="text-xs text-secondary-500 dark:text-secondary-400 mb-1">Impuesto</div>
                                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-secondary-100 dark:bg-secondary-700 text-secondary-800 dark:text-secondary-200">
                                            {getTaxRate(product.taxCode || '')}%
                                          </span>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Hover indicator */}
                                    <div className="absolute inset-0 border-2 border-primary-500 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <div className="text-center">
                            <div className="mx-auto h-16 w-16 text-secondary-300 dark:text-secondary-600">
                              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                            </div>
                            <p className="mt-4 text-base font-medium text-secondary-900 dark:text-white">No hay productos disponibles</p>
                            <p className="mt-1 text-sm text-secondary-500">Comienza agregando productos a tu catálogo</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>
            )}

            {/* Formulario de item */}
            {(!showProductSearch || itemForm.productId) && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Referencia */}
                  <Input
                    label="Referencia"
                    placeholder="Ej: REF-001"
                    value={itemForm.reference}
                    onChange={(e) => updateField('reference', e.target.value)}
                    data-testid="invoice-wizard-step3-reference-input"
                  />

                  {/* Código */}
                  <Input
                    label="Código"
                    placeholder="Código del producto"
                    value={itemForm.productCode}
                    onChange={(e) => updateField('productCode', e.target.value)}
                    maxLength={20}
                    required
                    data-testid="invoice-wizard-step3-product-code-input"
                  />
                </div>

                {/* Nombre del producto */}
                <Input
                  label="Nombre del Producto"
                  placeholder="Nombre del producto o servicio"
                  value={itemForm.productName}
                  onChange={(e) => updateField('productName', e.target.value)}
                  required
                  data-testid="invoice-wizard-step3-product-name-input"
                />

                {/* Descripción */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                    Descripción
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white"
                    rows={2}
                    placeholder="Descripción detallada del producto"
                    maxLength={500}
                    value={itemForm.description}
                    onChange={(e) => updateField('description', e.target.value)}
                    data-testid="invoice-wizard-step3-description-textarea"
                  />
                  <div className="flex justify-between mt-1">
                    <span className={`text-xs ${(itemForm.productName.length + (itemForm.description?.length || 0) + (itemForm.description ? 3 : 0)) > 450 ? 'text-danger-600' : 'text-secondary-400'}`}>
                      DGI máx. 500 caracteres (nombre + descripción)
                    </span>
                    <span className={`text-xs ${(itemForm.productName.length + (itemForm.description?.length || 0) + (itemForm.description ? 3 : 0)) > 500 ? 'text-danger-600 font-semibold' : 'text-secondary-400'}`}>
                      {itemForm.productName.length + (itemForm.description?.length || 0) + (itemForm.description ? 3 : 0)}/500
                    </span>
                  </div>
                </div>

                {/* Segmento y Familia (para facturas de gobierno) */}
                {wizardData.receiverType === '03' && (
                  <div className="border border-primary-200 dark:border-primary-700 rounded-lg p-4 bg-primary-50 dark:bg-primary-900/20">
                    <h4 className="text-sm font-semibold text-primary-700 dark:text-primary-300 mb-3">
                      Clasificación UNSPSC (Requerido para Gobierno)
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Segmento */}
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                          Segmento <span className="text-danger-600">*</span>
                        </label>
                        <select
                          className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white"
                          value={itemForm.segment || ''}
                          onChange={(e) => updateField('segment', e.target.value)}
                          data-testid="invoice-wizard-step3-segment-select"
                        >
                          <option value="">Seleccione un segmento</option>
                          {SEGMENTS.filter(s => s.Status).map((segment) => (
                            <option key={segment.Code} value={segment.Code.toString()}>
                              {segment.Code} - {segment.Description}
                            </option>
                          ))}
                        </select>
                        <p className="mt-1 text-xs text-secondary-500">
                          Código de segmento UNSPSC del producto
                        </p>
                      </div>

                      {/* Familia */}
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                          Familia <span className="text-danger-600">*</span>
                        </label>
                        <select
                          className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white ${
                            itemErrors.family
                              ? 'border-danger-500 dark:border-danger-500'
                              : 'border-secondary-300 dark:border-secondary-600'
                          }`}
                          value={itemForm.family || ''}
                          onChange={(e) => {
                            updateField('family', e.target.value);
                            if (itemErrors.family) setItemErrors((prev) => ({ ...prev, family: '' }));
                          }}
                          disabled={!itemForm.segment}
                          data-testid="invoice-wizard-step3-family-select"
                        >
                          <option value="">
                            {itemForm.segment ? 'Seleccione una familia' : 'Primero seleccione un segmento'}
                          </option>
                          {availableFamilies.map((family) => (
                            <option key={family.Code} value={family.Code}>
                              {family.Code} - {family.Description}
                            </option>
                          ))}
                        </select>
                        {itemErrors.family ? (
                          <p className="mt-1 text-xs text-danger-600" data-testid="invoice-wizard-step3-family-error">{itemErrors.family}</p>
                        ) : (
                          <p className="mt-1 text-xs text-secondary-500">
                            Código de familia UNSPSC del producto
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  {/* Cantidad */}
                  <Input
                    label="Cantidad"
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={itemForm.quantity}
                    onChange={(e) => {
                      const val = e.target.value;
                      // Permitir vacío temporalmente, pero guardar como número
                      updateField('quantity', val === '' ? 0 : parseFloat(val) || 0);
                    }}
                    onBlur={(e) => {
                      // Al salir del campo, asegurar mínimo de 0.01
                      const val = parseFloat(e.target.value) || 0;
                      if (val < 0.01) {
                        updateField('quantity', 1);
                      }
                    }}
                    onFocus={(e) => e.target.select()}
                    required
                    data-testid="invoice-wizard-step3-quantity-input"
                  />

                  {/* Unidad de medida (DGI C05 cUnidad - Tabla 29) */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                      Unidad
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white"
                      value={itemForm.unit || 'und'}
                      onChange={(e) => updateField('unit', e.target.value)}
                      data-testid="invoice-wizard-step3-unit-select"
                    >
                      <optgroup label="Contaje">
                        <option value="und">Unidad</option>
                        <option value="Persona">Persona</option>
                        <option value="Grupo">Grupo</option>
                        <option value="Paquete">Paquete</option>
                        <option value="Kit">Kit</option>
                        <option value="Caja">Caja</option>
                        <option value="Docena">Docena</option>
                        <option value="Ciento">Ciento</option>
                        <option value="Millar">Millar</option>
                        <option value="Viaje">Viaje</option>
                        <option value="Visita">Visita</option>
                      </optgroup>
                      <optgroup label="Tiempo">
                        <option value="Hora">Hora</option>
                        <option value="Dia">Día</option>
                        <option value="Semana">Semana</option>
                        <option value="Mes">Mes</option>
                        <option value="Ano">Año</option>
                      </optgroup>
                      <optgroup label="Trabajo">
                        <option value="Actividad">Actividad</option>
                        <option value="Hora trabajo">Hora de trabajo</option>
                      </optgroup>
                      <optgroup label="Masa">
                        <option value="g">Gramos (g)</option>
                        <option value="kg">Kilogramos (kg)</option>
                        <option value="lb">Libras (lb)</option>
                        <option value="oz">Onzas (oz)</option>
                        <option value="t">Tonelada (t)</option>
                      </optgroup>
                      <optgroup label="Longitud">
                        <option value="cm">Centímetros (cm)</option>
                        <option value="m">Metros (m)</option>
                        <option value="km">Kilómetros (km)</option>
                        <option value="in">Pulgadas (in)</option>
                        <option value="ft">Pies (ft)</option>
                      </optgroup>
                      <optgroup label="Área">
                        <option value="m2">M² (m2)</option>
                        <option value="ha">Hectárea (ha)</option>
                      </optgroup>
                      <optgroup label="Volumen">
                        <option value="ml">Mililitros (ml)</option>
                        <option value="l">Litros (l)</option>
                        <option value="m3">M³ (m3)</option>
                        <option value="gal">Galón (gal)</option>
                      </optgroup>
                      <optgroup label="Potencia">
                        <option value="kWh">Kilowatt hora (kWh)</option>
                      </optgroup>
                    </select>
                  </div>

                  {/* Precio unitario */}
                  <Input
                    label="Precio Unitario ($)"
                    type="text"
                    inputMode="decimal"
                    value={itemForm.unitPrice === 0 ? '' : itemForm.unitPrice}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty, numbers, and up to 6 decimal places (DGI: 1-9p2-6)
                      if (value === '' || /^\d*\.?\d{0,6}$/.test(value)) {
                        updateField('unitPrice', value === '' ? 0 : value);
                      }
                    }}
                    onBlur={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      // Preservar hasta 6 decimales (formato DGI 1-9p2-6), mínimo 2
                      updateField('unitPrice', value > 0 ? formatPriceDecimals(value) : 0);
                    }}
                    onFocus={(e) => {
                      setTimeout(() => e.target.select(), 0);
                    }}
                    placeholder="0.00"
                    required
                    data-testid="invoice-wizard-step3-unit-price-input"
                  />

                  {/* Descuento unitario */}
                  <div>
                    <Input
                      label="Descuento Unitario ($)"
                      type="text"
                      inputMode="decimal"
                      value={itemForm.unitDiscount === 0 ? '' : itemForm.unitDiscount}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty, numbers, and up to 6 decimal places (DGI: 1-9p2-6)
                        if (value === '' || /^\d*\.?\d{0,6}$/.test(value)) {
                          updateField('unitDiscount', value === '' ? 0 : value);
                          if (itemErrors.unitDiscount) setItemErrors({});
                        }
                      }}
                      onBlur={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        // Preservar hasta 6 decimales (formato DGI 1-9p2-6), mínimo 2
                        updateField('unitDiscount', value > 0 ? formatPriceDecimals(value) : 0);
                      }}
                      onFocus={(e) => {
                        setTimeout(() => e.target.select(), 0);
                      }}
                      placeholder="0.00"
                      error={itemErrors.unitDiscount}
                      data-testid="invoice-wizard-step3-unit-discount-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* ITBMS */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                      ITBMS (Tasa %)
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      value={itemForm.taxRate}
                      onChange={(e) => updateField('taxRate', parseFloat(e.target.value))}
                      data-testid="invoice-wizard-step3-tax-rate-select"
                    >
                      <option value="0">0% - Exento de ITBMS</option>
                      <option value="7">7% - Tasa general</option>
                      <option value="10">10% - Tasa especial</option>
                      <option value="15">15% - Tasa especial</option>
                    </select>
                    <p className="mt-1 text-xs text-secondary-500">
                      Seleccione la tasa de ITBMS aplicable
                    </p>
                  </div>

                  {/* ISC - Dropdown con tasas predefinidas */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                      ISC (Impuesto Selectivo al Consumo)
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white"
                      value={itemForm.iscRate}
                      onChange={(e) => updateField('iscRate', parseFloat(e.target.value))}
                      data-testid="invoice-wizard-step3-isc-rate-select"
                    >
                      {ISC_RATES.map((rate, index) => (
                        <option key={index} value={rate.value}>
                          {(rate.value * 100).toFixed(2)}% - {rate.description.substring(0, 80)}
                          {rate.description.length > 80 ? '...' : ''}
                        </option>
                      ))}
                    </select>
                    {itemForm.iscRate > 0 && (
                      <p className="mt-1 text-xs text-secondary-500">
                        Tasa seleccionada: {(itemForm.iscRate * 100).toFixed(2)}%
                      </p>
                    )}
                  </div>
                </div>

                {/* Sección OTI (Otros Tributos e Impuestos) */}
                <div className="border border-secondary-200 dark:border-secondary-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
                      OTI (Otros Tributos e Impuestos)
                    </h3>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleAddOTI}
                      leftIcon={<PlusIcon className="w-4 h-4" />}
                      data-testid="invoice-wizard-step3-add-oti-button"
                    >
                      Agregar OTI
                    </Button>
                  </div>

                  {itemForm.gOTIItem.length === 0 ? (
                    <p className="text-sm text-secondary-500 text-center py-3">
                      No hay OTI agregados. Haz clic en "Agregar OTI" para añadir uno.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {itemForm.gOTIItem.map((oti, index) => (
                        <div key={index} className="flex gap-3 items-start" data-testid={`invoice-wizard-step3-oti-row-${index}`}>
                          {/* Dropdown de código OTI */}
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                              Código OTI
                            </label>
                            <select
                              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white text-sm"
                              value={oti.dCodOTI}
                              onChange={(e) => handleUpdateOTI(index, 'dCodOTI', e.target.value)}
                              data-testid={`invoice-wizard-step3-oti-code-select-${index}`}
                            >
                              {OTI_CODES.map((code) => {
                                // DGI C601: Filtrar códigos ya usados en este ítem (excepto el actual)
                                const usedByOthers = itemForm.gOTIItem.some(
                                  (other, otherIdx) => otherIdx !== index && other.dCodOTI === code.code
                                );
                                return (
                                  <option key={code.code} value={code.code} disabled={usedByOthers}>
                                    {code.code} - {code.description}{usedByOthers ? ' (ya usado)' : ''}
                                  </option>
                                );
                              })}
                            </select>
                          </div>

                          {/* Input de valor */}
                          <div className="w-32">
                            <label className="block text-xs font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                              Valor ($)
                            </label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={parseFloat(oti.dValOTI) === 0 ? '' : oti.dValOTI}
                              onChange={(e) => handleUpdateOTI(index, 'dValOTI', e.target.value)}
                              onBlur={(e) => {
                                const value = parseFloat(e.target.value) || 0;
                                handleUpdateOTI(index, 'dValOTI', value.toFixed(2));
                              }}
                              onFocus={(e) => e.target.select()}
                              placeholder="0.00"
                              data-testid={`invoice-wizard-step3-oti-value-input-${index}`}
                            />
                          </div>

                          {/* Botón eliminar */}
                          <button
                            type="button"
                            onClick={() => handleRemoveOTI(index)}
                            className="mt-6 text-danger-600 hover:text-danger-900"
                            title="Eliminar OTI"
                            data-testid={`invoice-wizard-step3-remove-oti-button-${index}`}
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      ))}

                      {/* Mostrar total de OTI */}
                      {itemForm.gOTIItem.length > 0 && (
                        <div className="pt-2 border-t border-secondary-200 dark:border-secondary-700">
                          <div className="flex justify-between text-sm">
                            <span className="font-medium text-secondary-700 dark:text-secondary-300">
                              Total OTI:
                            </span>
                            <span className="font-semibold text-primary-600">
                              ${formatNumber(itemForm.gOTIItem.reduce((sum, oti) => sum + (parseFloat(oti.dValOTI) || 0), 0))}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Acarreo por Item */}
                  <Input
                    label="Acarreo por Item ($)"
                    type="text"
                    inputMode="decimal"
                    value={itemForm.shippingAmount === 0 ? '' : itemForm.shippingAmount}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty, numbers, and decimal point
                      if (value === '' || /^\d*\.?\d*$/.test(value)) {
                        updateField('shippingAmount', value === '' ? 0 : value);
                      }
                    }}
                    onBlur={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      // Always format with 2 decimals, show empty if 0
                      updateField('shippingAmount', value > 0 ? value.toFixed(2) : 0);
                    }}
                    onFocus={(e) => {
                      setTimeout(() => e.target.select(), 0);
                    }}
                    placeholder="0.00"
                    helperText="Costo de envío específico para este item"
                    data-testid="invoice-wizard-step3-shipping-amount-input"
                  />

                  {/* Seguro por Item */}
                  <Input
                    label="Seguro por Item ($)"
                    type="text"
                    inputMode="decimal"
                    value={itemForm.insuranceAmount === 0 ? '' : itemForm.insuranceAmount}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty, numbers, and decimal point
                      if (value === '' || /^\d*\.?\d*$/.test(value)) {
                        updateField('insuranceAmount', value === '' ? 0 : value);
                      }
                    }}
                    onBlur={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      // Always format with 2 decimals, show empty if 0
                      updateField('insuranceAmount', value > 0 ? value.toFixed(2) : 0);
                    }}
                    onFocus={(e) => {
                      setTimeout(() => e.target.select(), 0);
                    }}
                    placeholder="0.00"
                    helperText="Costo de seguro específico para este item"
                    data-testid="invoice-wizard-step3-insurance-amount-input"
                  />
                </div>

                {/* Información de Pedido/Orden (Opcional) */}
                <details ref={orderDetailsRef} className="border border-secondary-200 dark:border-secondary-700 rounded-lg">
                  <summary className="cursor-pointer px-4 py-3 bg-secondary-50 dark:bg-secondary-800 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-750 transition-colors">
                    <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
                      Información de Pedido/Orden (Opcional)
                    </span>
                  </summary>
                  <div className="p-4 space-y-3">
                    <Input
                      ref={orderNumberRef}
                      label="Número de Pedido"
                      value={itemForm.orderNumber || ''}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, '');
                        updateField('orderNumber', val);
                      }}
                      placeholder="Ej: 20240001"
                      inputMode="numeric"
                      maxLength={9}
                      required={!!(itemForm.orderItemSequence || itemForm.orderItemInfo)}
                      error={itemErrors.orderNumber}
                      helperText={!itemErrors.orderNumber ? "Solo números, máximo 9 dígitos (DGI dNroPed)" : undefined}
                      data-testid="invoice-wizard-step3-order-number-input"
                    />
                    <Input
                      label="Secuencia del Item en Pedido"
                      value={itemForm.orderItemSequence || ''}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, '');
                        updateField('orderItemSequence', val);
                      }}
                      placeholder="Ej: 1"
                      inputMode="numeric"
                      maxLength={3}
                      required={!!(itemForm.orderNumber || itemForm.orderItemInfo)}
                      error={itemErrors.orderItemSequence}
                      helperText={!itemErrors.orderItemSequence ? "Máximo 3 dígitos (DGI dSecItemPed)" : undefined}
                      data-testid="invoice-wizard-step3-order-item-sequence-input"
                    />
                    <Input
                      label="Información Adicional"
                      value={itemForm.orderItemInfo || ''}
                      onChange={(e) => updateField('orderItemInfo', e.target.value)}
                      placeholder="Información extra del pedido"
                      maxLength={5000}
                      required={!!(itemForm.orderNumber || itemForm.orderItemSequence)}
                      error={itemErrors.orderItemInfo}
                      helperText={!itemErrors.orderItemInfo ? "Cualquier información adicional relevante del pedido" : undefined}
                      data-testid="invoice-wizard-step3-order-item-info-input"
                    />
                  </div>
                </details>
              </>
            )}
            </div>

            {/* Columna Derecha - Resumen */}
            {(!showProductSearch || itemForm.productId) && (
              <div className="w-full lg:w-96 flex-shrink-0">
                <div className="sticky top-0">
                  <Card>
                    <CardBody className="space-y-4">
                      {/* Header */}
                      <div className="pb-3 border-b border-secondary-200 dark:border-secondary-700">
                        <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
                          Resumen del Item
                        </h3>
                      </div>

                      {/* Detalles del Producto */}
                      {itemForm.productName && (
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
                            Producto
                          </h4>
                          <p className="text-base font-semibold text-secondary-900 dark:text-white">
                            {itemForm.productName}
                          </p>
                          {itemForm.productCode && (
                            <p className="text-sm text-secondary-500">
                              Código: {itemForm.productCode}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Cálculos */}
                      <div className="space-y-3 py-3 border-t border-secondary-200 dark:border-secondary-700">
                        {/* Cantidad x Precio */}
                        <div className="flex justify-between text-sm">
                          <span className="text-secondary-600 dark:text-secondary-400">
                            Cantidad × Precio
                          </span>
                          <span className="font-medium text-secondary-900 dark:text-white">
                            {formatNumber(itemForm.quantity, 2)} × ${formatPriceDisplay(itemForm.unitPrice)}
                          </span>
                        </div>

                        {/* Subtotal */}
                        <div className="flex justify-between">
                          <span className="text-sm text-secondary-600 dark:text-secondary-400">
                            Subtotal:
                          </span>
                          <span className="text-base font-semibold text-secondary-900 dark:text-white">
                            ${formatPriceDisplay(calculatedValues.subtotal)}
                          </span>
                        </div>

                        {/* Descuento */}
                        {itemForm.unitDiscount > 0 && (
                          <div className="flex justify-between">
                            <span className="text-sm text-secondary-600 dark:text-secondary-400">
                              Descuento:
                            </span>
                            <span className="text-base font-semibold text-danger-600 dark:text-danger-400">
                              -${formatPriceDisplay(itemForm.unitDiscount * itemForm.quantity)}
                            </span>
                          </div>
                        )}

                        {/* ITBMS */}
                        <div className="flex justify-between">
                          <span className="text-sm text-secondary-600 dark:text-secondary-400">
                            ITBMS ({itemForm.taxRate}%):
                          </span>
                          <span className="text-base font-semibold text-secondary-900 dark:text-white">
                            ${formatPriceDisplay(calculatedValues.taxAmount)}
                          </span>
                        </div>

                        {/* ISC */}
                        {itemForm.iscRate > 0 && (
                          <div className="flex justify-between">
                            <span className="text-sm text-secondary-600 dark:text-secondary-400">
                              ISC ({(itemForm.iscRate * 100).toFixed(2)}%):
                            </span>
                            <span className="text-base font-semibold text-secondary-900 dark:text-white">
                              ${formatPriceDisplay(calculatedValues.iscAmount)}
                            </span>
                          </div>
                        )}

                        {/* OTI */}
                        {itemForm.gOTIItem.length > 0 && (() => {
                          const otiTotal = itemForm.gOTIItem.reduce((sum, oti) => sum + (parseFloat(oti.dValOTI) || 0), 0);
                          return (
                            <div className="flex justify-between">
                              <span className="text-sm text-secondary-600 dark:text-secondary-400">
                                OTI ({itemForm.gOTIItem.length}):
                              </span>
                              <span className="text-base font-semibold text-secondary-900 dark:text-white">
                                ${formatNumber(otiTotal)}
                              </span>
                            </div>
                          );
                        })()}

                        {/* Acarreo */}
                        {itemForm.shippingAmount > 0 && (
                          <div className="flex justify-between">
                            <span className="text-sm text-secondary-600 dark:text-secondary-400">
                              Acarreo:
                            </span>
                            <span className="text-base font-semibold text-secondary-900 dark:text-white">
                              ${formatNumber(itemForm.shippingAmount)}
                            </span>
                          </div>
                        )}

                        {/* Seguro */}
                        {itemForm.insuranceAmount > 0 && (
                          <div className="flex justify-between">
                            <span className="text-sm text-secondary-600 dark:text-secondary-400">
                              Seguro:
                            </span>
                            <span className="text-base font-semibold text-secondary-900 dark:text-white">
                              ${formatNumber(itemForm.insuranceAmount)}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Total */}
                      <div className="pt-3 border-t-2 border-secondary-300 dark:border-secondary-600">
                        <div className="flex justify-between items-center">
                          <span className="text-lg font-bold text-secondary-900 dark:text-white">
                            Total:
                          </span>
                          <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                            ${formatPriceDisplay(calculatedValues.total)}
                          </span>
                        </div>
                      </div>

                      {/* Botones de Acción */}
                      <div className="flex flex-col gap-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
                        <Button
                          variant="primary"
                          onClick={handleSaveItem}
                          disabled={!itemForm.productCode || !itemForm.productName || itemForm.quantity <= 0}
                          className="w-full"
                          data-testid="invoice-wizard-step3-save-item-button"
                        >
                          {editingIndex !== null ? 'Actualizar Item' : 'Agregar Item'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={handleCloseModal}
                          className="w-full"
                          data-testid="invoice-wizard-step3-cancel-item-button"
                        >
                          Cancelar
                        </Button>
                      </div>
                    </CardBody>
                  </Card>
                </div>
              </div>
            )}
          </div>
        </ModalBody>
      </Modal>

      {/* Product Form Modal */}
      <ProductFormModal
        isOpen={isCreateProductModalOpen}
        onClose={() => {
          console.log('🔵 ProductFormModal onClose called');
          setIsCreateProductModalOpen(false);
        }}
        onProductCreated={(product) => {
          console.log('✅ Product created, auto-selecting');
          // Auto-select the newly created product
          handleSelectProduct(product);
          setIsCreateProductModalOpen(false);
        }}
      />
    </div>
  );
}
